/**
 * WCC360 v2 — Premium career drill-down.
 *
 * The card, opened up. Everything on this page comes from the careers bundle
 * (real for all 5,877 rows) and the Supabase market layer. The Registry
 * content layer is rendered only where it carries genuine values — 5,777 of
 * 5,877 rows are empty templates, and a page of "Evidence pending" is worse
 * than no section at all.
 *
 * Presentation only. No scoring here, no writes anywhere.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Info, Loader2, Lock, RefreshCw, Sparkles, Star,
} from "lucide-react";
import { AssessmentV2Provider, useAssessmentV2 } from "@/features/assessment-v2/store";
import { loadLatestCompletedRun } from "@/features/assessment-v2/persistedSnapshot";
import { ReplayRetryNotice } from "@/components/career/ReplayRetryNotice";
import type { AssessmentV2State } from "@/features/assessment-v2/types";
import {
  ARCHETYPE_TAGLINES, sentenceCase, CONTEXT_LABELS, DISTANCE_STYLES, VALUES_LABELS,
  composeLearner, formatSalary,
} from "@/features/assessment-v2/results";
import { loadCareersBundle, type CareerRow, type CareersBundle } from "@/data/careersBundle";
import { bundleToCareers } from "@/lib/careersToScoring";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import {
  effectiveCapacity, entryCost, matchCareers,
  type Archetype, type Career, type Learner, type MatchResult,
} from "@/lib/scoring";
import {
  DAY_SIGNALS, ENERGY_SIGNALS, VALUE_SIGNALS, DEMAND_COPY, FIT_CLASS, FIT_COPY,
  automationCopy, buildSignalRows, marketSourceCopy, type SignalRow,
} from "@/lib/careerProse";
import { resolveCareerContent, humanizeKey, SECTION_LABELS, SECTION_ORDER } from "@/lib/careerContent";
import { deriveDayShape, pairCopy, type Signals } from "@/data/careerCopy";
import { getDayInLife } from "@/data/dayInLife";
import DayInLifeTimeline from "@/components/career/DayInLifeTimeline";
import { loadCareerIntel, type CareerIntel } from "@/lib/careerIntel";
import {
  loadPromotedEnrichment,
  applyEnrichment,
  ENRICHED_FIELD_LABELS,
  type CareerEnrichment,
} from "@/lib/careerEnrichment";

import {
  AutomationOutlook, DemandForecast, GettingIn, ImpactPanel, IntelProvenance,
  MovesFromHere, PayAndConditions, TheWork, WhoThrives,
} from "@/components/career/CareerIntel";
import { CareerPathways, CareerRoadmapTimeline } from "@/components/career/CareerRoadmap";
import { CountryRoadmap, USResources } from "@/components/career/CountryRoadmap";
import { useLearnerCountry } from "@/hooks/useLearnerCountry";
import { COUNTRY_LABEL } from "@/lib/learnerCountry";

import { useMembership } from "@/hooks/useMembership";
import { cloudStorage } from "@/lib/cloudStore";
import GrowthPathway, { type CareerJourney } from "@/components/career/GrowthPathway";
import RecommendedCerts from "@/components/certifications/RecommendedCerts";
import PinToVisionBoard from "@/components/career/PinToVisionBoard";
import OccupationStandardsNote from "@/components/OccupationStandardsNote";
import OnetAttribution from "@/components/OnetAttribution";
import OccupationCoding from "@/components/OccupationCoding";


const SAVED_KEY = "wcc360.v2.saved_careers";

/* ------------------------------------------------------------------ */
/* Small shared pieces                                                 */
/* ------------------------------------------------------------------ */

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{children}</div>
  );
}

function Section({
  id, eyebrow, title, subtitle, children,
}: {
  id: string; eyebrow: string; title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-12 scroll-mt-24">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display text-2xl md:text-3xl font-medium">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function ScoreBar({ value }: { value: number }) {
  const tone =
    value >= 75 ? "bg-emerald-400" : value >= 55 ? "bg-sky-400" : "bg-amber-400";
  return (
    <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
      <div
        className={`h-full rounded-full ${tone}`}
        style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
      />
    </div>
  );
}

function FitChip({ row }: { row: SignalRow }) {
  if (!row.fit) return null;
  return (
    <span
      className={`shrink-0 text-[11px] font-medium px-2 py-1 rounded-full border ${FIT_CLASS[row.fit]}`}
    >
      {FIT_COPY[row.fit]}
    </span>
  );
}

function SignalList({ rows }: { rows: SignalRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-3xl border border-border bg-card divide-y divide-border">
      {rows.map((row) => (
        <div key={row.label} className="p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {row.label}
            </div>
            <FitChip row={row} />
          </div>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground/90">{row.sentence}</p>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Premium teaser                                                      */
/* ------------------------------------------------------------------ */

/**
 * Single switch for the career drill-down paywall.
 * Set to `true` to blur sections 3–9 for non-members again once tiers
 * are properly set up. Gating logic below is intentionally kept intact.
 */
const CAREER_DETAIL_GATED = false;

function LockedTeaser({ children }: { children: React.ReactNode }) {

  const navigate = useNavigate();
  return (
    <div className="relative mt-12">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none opacity-45 blur-[3px] max-h-[1400px] overflow-hidden"
      >
        {children}
      </div>
      <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-background/10 via-background/60 to-background pointer-events-none" />
      <div className="absolute inset-x-0 top-10 flex justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-primary/30 bg-card/95 backdrop-blur-xl p-6 text-center shadow-2xl">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-primary/15 text-primary border border-primary/30">
            <Sparkles className="h-3.5 w-3.5" /> Full career deep-dive
          </span>
          <h3 className="mt-4 font-display text-xl md:text-2xl font-medium">
            There's a lot more to this one.
          </h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Below this you'd see the day itself — what you'd be exposed to, what it asks of your
            energy, what it pays and protects, who does well here, and exactly how you'd get in.
          </p>
          <button
            onClick={() => navigate("/payment")}
            className="mt-5 w-full min-h-[48px] rounded-2xl bg-primary text-primary-foreground font-medium
              inline-flex items-center justify-center gap-2 hover:opacity-90"
          >
            Unlock the full picture <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-3 text-[11px] text-muted-foreground inline-flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3" /> One payment. Twelve months of access.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Data assembly                                                       */
/* ------------------------------------------------------------------ */

interface Neighbour {
  ciId: string;
  title: string;
  level: string;
  salary: string;
  distance: string | null;
}

/**
 * Where the signals on this page came from.
 * - `local`     — the run finished on this device and is current.
 * - `backend`   — replayed from the learner's latest completed run.
 * - `refreshed` — this device held an older run; we replaced it with the newer
 *                 backend one and told the learner.
 */
type MatchSource = "local" | "backend" | "refreshed";

interface Loaded {
  row: CareerRow;
  career: Career;
  learner: Learner | null;
  match: MatchResult | null;
  distance: string;
  neighbours: Neighbour[];
  capacity: number | null;
  need: number;
  matchSource: MatchSource | null;
  /** ISO timestamp of the run the signals came from, when known. */
  matchCompletedAt: string | null;
  /** Career name (lowercased) → ci_id, so related roles can link out. */
  nameIndex: Map<string, string>;
}



function hasAssessment(part2: Record<string, unknown>): boolean {
  return Boolean(part2 && (part2.archetype_tally || part2.slots));
}

function dnaDistance(a: Career, b: Career): number {
  const keys = new Set([...Object.keys(a.dna), ...Object.keys(b.dna)]) as Set<Archetype>;
  let d = 0;
  for (const k of keys) d += Math.abs((a.dna[k] ?? 0) - (b.dna[k] ?? 0));
  return d;
}

const DISTANCE_MEANING: Record<string, string> = {
  "Lateral move": "You could start applying now. Weeks, not years.",
  "Short reskill": "A short course — think three to six months alongside what you already do.",
  "Retrain 1-2 yrs": "One to two years of study, part-time or full.",
  "Full requalification": "Three years or more. This is a full qualification, not a course.",
};

/* ------------------------------------------------------------------ */
/* Content-layer filter — only real values survive                     */
/* ------------------------------------------------------------------ */

function isRealField(raw: unknown): boolean {
  if (raw == null) return false;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (typeof o.classification === "string" && o.classification === "evidence_pending") return false;
    const v = "value" in o ? o.value : undefined;
    if (v == null) return false;
    if (typeof v === "string" && (!v.trim() || /pending|template_registered/i.test(v))) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }
  if (typeof raw === "string") return Boolean(raw.trim()) && !/pending|template_registered/i.test(raw);
  if (Array.isArray(raw)) return raw.length > 0;
  return true;
}

/**
 * Keys the page already renders natively, in a better shape than a label +
 * paragraph. Skipping them here stops the registry panel repeating the day
 * timeline, the archetype split and the salary band as raw JSON.
 */
const STRUCTURAL_KEYS = new Set([
  "week_shape",
  "day_shape",
  "combined_reality",
  "archetype_dna",
  "salary_band_lpa",
]);

function realSections(
  sections: Record<string, Record<string, unknown>> | undefined,
): Array<{ key: string; fields: Array<[string, unknown]> }> {
  if (!sections) return [];
  const keys = [
    ...SECTION_ORDER.filter((k) => k in sections),
    ...Object.keys(sections).filter((k) => !SECTION_ORDER.includes(k)),
  ];
  const out: Array<{ key: string; fields: Array<[string, unknown]> }> = [];
  for (const key of keys) {
    const body = sections[key];
    if (!body || typeof body !== "object") continue;
    if (typeof body.status === "string" && body.status === "template_registered") continue;
    const fields = Object.entries(body).filter(
      ([k, v]) =>
        !["status", "population", "classification", "provenance"].includes(k) &&
        !STRUCTURAL_KEYS.has(k) &&
        isRealField(v),
    );
    if (fields.length) out.push({ key, fields });
  }
  return out;
}


function contentText(raw: unknown): string | null {
  const v =
    raw && typeof raw === "object" && !Array.isArray(raw) && "value" in (raw as object)
      ? (raw as Record<string, unknown>).value
      : raw;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    const parts = v.map((x) => contentText(x)).filter(Boolean) as string[];
    return parts.length ? parts.join(" · ") : null;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

function CareerDetailInner() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useAssessmentV2();
  const membership = useMembership();
  const { country: learnerCountry } = useLearnerCountry();

  const [data, setData] = useState<Loaded | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Replay health: failures are recoverable, so they live outside `err`. */
  const [replayFailed, setReplayFailed] = useState(false);
  const [replayAttempt, setReplayAttempt] = useState(0);
  const [replayRetrying, setReplayRetrying] = useState(false);
  /** Read inside the loader without making the retry flag a dependency. */
  const retryingRef = useRef(false);
  /** Retry the backend replay, forcing a fresh fetch past the cache. */
  const retryReplay = () => {
    retryingRef.current = true;
    setReplayRetrying(true);
    setReplayAttempt((n) => n + 1);
  };
  const [intel, setIntel] = useState<CareerIntel | null>(null);
  const [journey, setJourney] = useState<CareerJourney>(null);
  const [enrichment, setEnrichment] = useState<CareerEnrichment | null>(null);
  const [content, setContent] = useState<
    Array<{ key: string; fields: Array<[string, unknown]> }>
  >([]);


  const [saved, setSaved] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(cloudStorage.getItem(SAVED_KEY) ?? "[]")); }
    catch { return new Set(); }
  });
  const toggleSaved = (cid: string) => {
    setSaved((s) => {
      const next = new Set(s);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      try { cloudStorage.setItem(SAVED_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const backTo = (location.state as { from?: string } | null)?.from ?? "/careers";

  useEffect(() => {
    let cancelled = false;
    // A replay retry keeps the page on screen — only a genuine navigation or a
    // fresh answer set clears it back to the loader.
    if (!retryingRef.current) {
      setData(null);
      setContent([]); setJourney(null);
      setIntel(null);
      setEnrichment(null);
    }
    setNotFound(false);
    setErr(null);


    (async () => {
      try {
        const [bundle, market] = await Promise.all([
          loadCareersBundle(),
          loadMarketIntelligence(),
        ]);
        if (cancelled) return;

        let ciId = id.trim();
        let row = findRow(bundle, ciId);

        // Legacy `WK` alias → ci_id. The content layer is used for the mapping
        // only, never for the body of this page.
        if (!row && ciId && !ciId.toUpperCase().startsWith("CI-")) {
          const mapped = await resolveCareerContent(ciId);
          if (cancelled) return;
          if (mapped?.ci_id) {
            ciId = mapped.ci_id;
            row = findRow(bundle, ciId);
          }
        }
        if (!row) { if (!cancelled) setNotFound(true); return; }

        const lookup = (cid: string) => market.get(cid);
        const careers = bundleToCareers(bundle, lookup);
        const byId = new Map(careers.map((c) => [c.ciId, c]));
        const career = byId.get(row.ciId)!;

        // The provider starts with this device's saved state. The cached
        // backend replay is consulted either way: when local state is empty it
        // is the only source, and when local state is older than the learner's
        // latest completed run it supersedes it (and we say so on the page).
        const localOk = hasAssessment(state.part2);
        // The replay is a helper, never a gate: if it fails the career still
        // renders, we fall back to this device's answers and offer a retry.
        let run: Awaited<ReturnType<typeof loadLatestCompletedRun>> = null;
        try {
          run = await loadLatestCompletedRun(retryingRef.current ? { force: true } : {});
          if (cancelled) return;
          setReplayFailed(false);
        } catch {
          if (cancelled) return;
          setReplayFailed(true);
        } finally {
          retryingRef.current = false;
          if (!cancelled) setReplayRetrying(false);
        }


        let matchSource: MatchSource | null = null;
        let assessmentState: AssessmentV2State | null = null;
        let matchCompletedAt: string | null = null;

        const runIsNewer =
          run?.completedAt != null &&
          (!state.updatedAt || Date.parse(run.completedAt) > Date.parse(state.updatedAt));

        if (localOk && !runIsNewer) {
          assessmentState = state;
          matchSource = "local";
          matchCompletedAt = state.updatedAt ?? null;
        } else if (run && hasAssessment(run.state.part2)) {
          assessmentState = run.state;
          matchSource = localOk ? "refreshed" : "backend";
          matchCompletedAt = run.completedAt;
        } else if (localOk) {
          assessmentState = state;
          matchSource = "local";
          matchCompletedAt = state.updatedAt ?? null;
        }

        const learner = assessmentState && hasAssessment(assessmentState.part2)
          ? composeLearner(assessmentState)
          : null;
        if (!learner) { matchSource = null; matchCompletedAt = null; }


        // One-career match keeps the engine as the only source of tier and
        // distance. The family cap in matchCareers can't bite on a single row.
        let match: MatchResult | null = null;
        if (learner) {
          match = matchCareers(learner, [career], 1).results[0] ?? null;
        }
        const need = entryCost(career);
        const capacity = learner ? effectiveCapacity(learner) : null;
        const distance = match?.distance ?? "Full requalification";

        // Careers next door — same family first, cluster as backstop.
        const pool = careers.filter(
          (c) =>
            c.ciId !== career.ciId &&
            ((career.family && c.family === career.family) ||
              (career.cluster && c.cluster === career.cluster)),
        );
        const near = pool
          .map((c) => ({ c, d: dnaDistance(career, c) }))
          .sort((a, b) => a.d - b.d || (a.c.title < b.c.title ? -1 : 1))
          .slice(0, 6)
          .map(({ c }) => ({
            ciId: c.ciId,
            title: c.title,
            level: c.level,
            salary: formatSalary(c.salaryLo, c.salaryHi),
            distance: learner ? matchCareers(learner, [c], 1).results[0]?.distance ?? null : null,
          }));

        const nameIndex = new Map(careers.map((c) => [c.title.trim().toLowerCase(), c.ciId]));

        if (cancelled) return;
        setData({
          row, career, learner, match, distance, neighbours: near, capacity, need,
          matchSource, matchCompletedAt, nameIndex,
        });

        // v3.2 Career Intelligence, overlaid with web-researched copy for
        // emerging roles. Both are optional and never block the page.
        Promise.all([
          loadCareerIntel(row.name).catch(() => null),
          loadPromotedEnrichment(row.ciId).catch(() => null),
        ]).then(([i, e]) => {
          if (cancelled) return;
          setEnrichment(e);
          setIntel(applyEnrichment(i, e, row.name));
        });

        // Content layer, last and optional. Failure here changes nothing.
        resolveCareerContent(row.ciId)
          .then((c) => { if (!cancelled && c) { setContent(realSections(c.sections)); setJourney((c.sections as any)?.career_journey ?? null); } })
          .catch(() => { /* content layer is optional */ });

      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Something didn't load.");
      }
    })();

    return () => { cancelled = true; };
  }, [id, state, replayAttempt]);

  const rows = useMemo(() => {
    if (!data) return null;
    const f = data.row.fields as Record<string, string | null | undefined>;
    return {
      day: buildSignalRows(DAY_SIGNALS, f, data.learner),
      energy: buildSignalRows(ENERGY_SIGNALS, f, data.learner),
      values: buildSignalRows(VALUE_SIGNALS, f, data.learner),
    };
  }, [data]);

  /**
   * A hand-written practitioner persona is used ONLY on an exact title match —
   * it was written about that one role. Everything else gets a day shape
   * derived from this career's own signals, never another role's persona.
   */
  const dayShape = useMemo(() => {
    if (!data) return null;
    const exact = getDayInLife(data.row.name);
    if (exact) return exact;
    return deriveDayShape(data.row.fields as Signals, {
      title: data.row.name,
      family: data.row.family,
    });
  }, [data]);

  const pairs = useMemo(
    () => (data ? pairCopy(data.row.fields as Signals) : []),
    [data],
  );



  const signals = useMemo(() => {
    if (!data?.match) return [];
    const m = data.match;
    const all = [
      ...Object.entries(m.contextDetail).map(([k, v]) => ({
        key: k, score: v, copy: CONTEXT_LABELS[k],
      })),
      ...Object.entries(m.valuesDetail).map(([k, v]) => ({
        key: k, score: v, copy: VALUES_LABELS[k],
      })),
    ].filter((s) => s.copy);
    return all.sort((a, b) => b.score - a.score);
  }, [data]);

  if (err) {
    return (
      <Shell backTo={backTo}>
        <div className="py-24 text-center space-y-4">
          <h1 className="font-display text-2xl">That didn't load just now.</h1>
          <p className="text-sm text-muted-foreground">{err}</p>
          <button
            onClick={() => navigate(backTo)}
            className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground"
          >
            Go back
          </button>
        </div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell backTo={backTo}>
        <div className="py-24 text-center space-y-3">
          <h1 className="font-display text-2xl">We don't have that career.</h1>
          <p className="text-sm text-muted-foreground">
            Nothing matched <code className="mx-1">{id}</code> in our registry of 5,877 careers.
          </p>
          <Link to="/careers" className="text-primary hover:underline text-sm">
            Browse all careers
          </Link>
        </div>
      </Shell>
    );
  }

  if (!data || !rows || !dayShape) {
    return (
      <Shell backTo={backTo}>
        <div className="py-24 grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </Shell>
    );
  }

  const { row, career, learner, match, distance, neighbours, matchSource, matchCompletedAt } = data;
  const sourceLabel =
    matchSource === "local"
      ? "From your run on this device"
      : matchSource === "backend"
        ? "Replayed from your saved run"
        : matchSource === "refreshed"
          ? "Refreshed from your latest run"
          : null;
  const distanceClass =
    DISTANCE_STYLES[distance] ?? "bg-secondary text-muted-foreground border-border";
  const salaryLabel = formatSalary(career.salaryLo, career.salaryHi);
  const marketNote = marketSourceCopy(career.market, career.marketStale, career.observations);
  const isSaved = saved.has(row.ciId);
  const premium = membership.isActive;

  const lessSure: string[] = [];
  if (career.confidence === "low")
    lessSure.push("Our scoring evidence for this role is thin, so we've held it out of the best-match tier. It may still fit you well.");
  if (career.market === "unpriced")
    lessSure.push("We have no salary evidence for this role. We've scored pay as neutral rather than guessing.");
  if (career.marketStale)
    lessSure.push("The salary figure was last refreshed more than a day ago. We've used it anyway — last week's number beats no number.");

  const resolveName = (name: string) =>
    data.nameIndex.get(name.trim().toLowerCase()) ?? null;

  /* ---------------- gated block ---------------- */
  const gated = (
    <>
      {/* 3 · The work itself — v3.2 intelligence */}
      {intel && (
        <>
          <Section
            id="work"
            eyebrow="The work itself"
            title="What you'd actually be doing"
            subtitle="Straight from the career registry, not a job advert."
          >
            <TheWork intel={intel} />
          </Section>

          <Section
            id="pay"
            eyebrow="Pay and conditions"
            title="What it pays, and how it's worked"
          >
            <PayAndConditions intel={intel} />
          </Section>

          <Section
            id="ai"
            eyebrow="AI and automation"
            title="How AI is likely to reshape this"
            subtitle="We'd rather you knew now than found out in ten years."
          >
            <AutomationOutlook intel={intel} />
          </Section>
        </>
      )}

      {/* 4 · A day in this work */}

      <Section
        id="day"
        eyebrow="A day in this work"
        title="What the day actually feels like"
        subtitle={
          learner
            ? "Nine things about the working day, each measured against what you told us."
            : "Nine things about the working day."
        }
      >
        <div className="space-y-4">
          <DayInLifeTimeline data={dayShape} />
          <SignalList rows={rows.day} />
          {pairs.length > 0 && (
            <div className="rounded-3xl border border-border bg-card p-5 md:p-6 space-y-3">
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                Worth reading together
              </div>
              {pairs.map((p) => (
                <p key={p} className="text-[15px] leading-relaxed text-foreground/90">{p}</p>
              ))}
            </div>
          )}
        </div>
      </Section>


      {/* 4 · Energy */}
      <Section
        id="energy"
        eyebrow="What it asks of you"
        title="What it asks of your energy"
        subtitle="Including the parts nobody puts in a job advert."
      >
        <SignalList rows={rows.energy} />
      </Section>

      {/* 5 · Pay and protection */}
      <Section
        id="market"
        eyebrow="Pay and protection"
        title="What it pays, and what it protects"
      >
        <div className="rounded-3xl border border-border bg-card p-5 md:p-6">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <div className="font-display text-2xl md:text-3xl font-medium tabular-nums">
              {salaryLabel}
            </div>
            {career.market !== "unpriced" && (
              <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                {career.market === "live" ? "Live postings" : "Estimate"}
              </span>
            )}
          </div>
          {marketNote && (
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{marketNote}</p>
          )}
          <div className="mt-5 space-y-3 border-t border-border pt-5">
            {career.demand2040 && DEMAND_COPY[career.demand2040] && (
              <p className="text-[15px] text-foreground/90 leading-relaxed">
                {DEMAND_COPY[career.demand2040]}
              </p>
            )}
            {typeof career.automationExposure === "number" && (
              <p className="text-[15px] text-foreground/90 leading-relaxed">
                {automationCopy(Math.round(career.automationExposure))}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <SignalList rows={rows.values} />
        </div>
      </Section>

      {/* 6 · Who does well here */}
      <Section
        id="archetypes"
        eyebrow="Who does well here"
        title="The kind of person this work rewards"
      >
        <div className="rounded-3xl border border-border bg-card p-5 md:p-6 space-y-4">
          {row.archetypes.map((a) => {
            const mine =
              learner &&
              (a.name === learner.archetypePrimary || a.name === learner.archetypeSecondary);
            return (
              <div key={a.name}>
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium">{a.name}</span>
                    {mine && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border border-primary/30 bg-primary/10 text-primary">
                        That's you
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                    {a.pct}%
                  </span>
                </div>
                <ScoreBar value={a.pct} />
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {ARCHETYPE_TAGLINES[a.name as Archetype]
                    ? `${sentenceCase(ARCHETYPE_TAGLINES[a.name as Archetype])}.`
                    : null}
                </p>
              </div>
            );
          })}
          {learner && (
            <p className="pt-4 border-t border-border text-sm text-muted-foreground leading-relaxed">
              You came out mostly <span className="text-foreground">{learner.archetypePrimary}</span>,
              and also <span className="text-foreground">{learner.archetypeSecondary}</span>.
              Overlap with the bars above is the strongest single signal in your match.
            </p>
          )}
        </div>
        {intel && (
          <div className="mt-4">
            <WhoThrives intel={intel} />
          </div>
        )}
      </Section>

      {/* 7 · How you'd get in */}
      <Section id="entry" eyebrow="Getting in" title="How you'd get in">
        <div className="rounded-3xl border border-border bg-card p-5 md:p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">
              What's asked for
            </div>
            <p className="text-[15px] leading-relaxed text-foreground/90">
              {row.entryRequirement?.trim() ||
                "No single entry route is fixed for this role. People arrive here from several directions."}
            </p>
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${distanceClass}`}>
                {distance}
              </span>
            </div>
            <p className="text-[15px] leading-relaxed text-foreground/90">
              {DISTANCE_MEANING[distance]}
            </p>
            {learner && data.capacity != null && (
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {data.need <= data.capacity
                  ? "That sits inside the study you told us you could take on."
                  : "That's more study than you said you could take on right now — which is a today problem, not a forever one."}
              </p>
            )}
          </div>
        </div>
        {intel && (
          <div className="mt-4">
            <GettingIn intel={intel} />
          </div>
        )}
      </Section>

      {/* 7· Country roadmap (US / India) */}
      <Section
        id="your-roadmap"
        eyebrow={`Your roadmap · ${COUNTRY_LABEL[learnerCountry]}`}
        title="How to get there, step by step"
        subtitle="Researched from trusted sources for where you are. Each step says what to expect."
      >
        <CountryRoadmap ciId={row.ciId} country={learnerCountry} />
        {learnerCountry === "US" && (
          <div className="mt-6">
            <USResources />
          </div>
        )}
      </Section>

      {/* 7a · Pathways and roadmap */}
      {intel && (
        <>
          <Section
            id="pathways"
            eyebrow="Pathways"
            title="The routes in, cheapest first"
            subtitle="More than one road arrives here. Take the one you can afford."
          >
            <CareerPathways intel={intel} />
          </Section>

          <Section
            id="roadmap"
            eyebrow="Roadmap"
            title="What to do, in what order"
            subtitle="Open any step to see the actions. Nothing here is fixed."
          >
            <CareerRoadmapTimeline intel={intel} />
          </Section>
        </>
      )}


      {/* 7a+ · Growth pathway */}
      <Section
        id="growth"
        eyebrow="Growth pathway"
        title="How this career grows with you"
        subtitle="Where you'd start, what each step asks of you, and where it can lead."
      >
        <GrowthPathway
          title={row.name}
          level={row.level}
          journey={journey}
          adjacent={intel?.adjacent ?? []}
          stretch={intel?.stretch ?? []}
          entrepreneurship={intel?.entrepreneurship ?? null}
          resolve={resolveName}
          from={location.pathname}
        />
      </Section>

      <Section
        id="certifications"
        eyebrow="Recommended certifications"
        title="Certifications that help you get here"
        subtitle="NSDC-approved, industry-recognised credentials matched to this role."
      >
        <RecommendedCerts ciId={row.ciId} limit={6} />
      </Section>

      {/* 7b · What this work changes */}
      {intel && (
        <Section
          id="impact"
          eyebrow="Why it matters"
          title="What this work changes"
        >
          <ImpactPanel intel={intel} />
        </Section>
      )}

      {/* 7c · Where it can take you */}
      {intel && (intel.adjacent.length > 0 || intel.stretch.length > 0) && (
        <Section
          id="moves"
          eyebrow="Where it can take you"
          title="Moves from here"
          subtitle="Each one opens as its own full page."
        >
          <MovesFromHere intel={intel} resolve={resolveName} from={location.pathname} />
        </Section>
      )}



      {/* 8 · Careers next door */}
      {neighbours.length > 0 && (
        <Section
          id="neighbours"
          eyebrow="Careers next door"
          title="If this one appeals, look at these"
          subtitle="Closest in the kind of person they suit."
        >
          <div className="rounded-3xl border border-border bg-card divide-y divide-border overflow-hidden">
            {neighbours.map((n) => (
              <Link
                key={n.ciId}
                to={`/careers/${n.ciId}`}
                state={{ from: location.pathname }}
                className="flex items-center justify-between gap-3 p-4 md:p-5 min-h-[56px]
                  hover:bg-secondary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {n.salary}
                    {n.level ? ` · ${n.level}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {n.distance && (
                    <span
                      className={`hidden sm:inline text-[11px] font-medium px-2 py-1 rounded-full border ${
                        DISTANCE_STYLES[n.distance] ?? "bg-secondary text-muted-foreground border-border"
                      }`}
                    >
                      {n.distance}
                    </span>
                  )}
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* 9 · What we're less sure about */}
      {lessSure.length > 0 && (
        <Section
          id="uncertainty"
          eyebrow="Honesty"
          title="What we're less sure about"
        >
          <div className="rounded-3xl border border-border bg-card p-5 md:p-6 space-y-3">
            {lessSure.map((s) => (
              <p key={s} className="text-[15px] leading-relaxed text-foreground/90">{s}</p>
            ))}
          </div>
        </Section>
      )}

      {/* Content layer — only when it carries real values */}
      {content.length > 0 && (
        <Section
          id="registry"
          eyebrow="From the registry"
          title="More on this role"
        >
          <div className="space-y-4">
            {content.map(({ key, fields }) => (
              <div key={key} className="rounded-3xl border border-border bg-card p-5 md:p-6">
                <h3 className="font-display text-lg font-medium mb-3">
                  {SECTION_LABELS[key] ?? humanizeKey(key)}
                </h3>
                <div className="space-y-3">
                  {fields.map(([field, raw]) => {
                    const text = contentText(raw);
                    if (!text) return null;
                    return (
                      <div key={field}>
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                          {humanizeKey(field)}
                        </div>
                        <p className="mt-1 text-[15px] leading-relaxed text-foreground/90">{text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );

  return (
    <Shell backTo={backTo}>
      {/* 1 · Hero */}
      <section className="pt-8 md:pt-12">
        <div className="flex flex-wrap items-center gap-2">
          {match && (
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${distanceClass}`}>
              {distance}
            </span>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">{salaryLabel}</span>
          {sourceLabel && (
            <span
              data-testid="match-source"
              data-source={matchSource ?? ""}
              title={matchCompletedAt ? `Answers from ${formatRunDate(matchCompletedAt)}` : undefined}
              className="text-xs text-muted-foreground inline-flex items-center gap-1 px-2.5 py-1
                rounded-full border border-border"
            >
              {matchSource === "local" ? <Star className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
              {sourceLabel}
            </span>
          )}
          {(career.confidence === "low" || career.market === "unpriced") && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Info className="h-3 w-3" /> We know less about this one
            </span>
          )}
        </div>
        {replayFailed && (
          <ReplayRetryNotice
            attempt={replayAttempt + 1}
            retrying={replayRetrying}
            hasLocalFallback={matchSource != null}
            onRetry={retryReplay}
          />
        )}
        {matchSource === "refreshed" && (
          <p
            role="status"
            data-testid="stale-refresh-notice"
            className="mt-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground/90"
          >
            This device was showing older answers. We've refreshed the signals below from your
            latest completed run{matchCompletedAt ? ` (${formatRunDate(matchCompletedAt)})` : ""}.
          </p>
        )}

        <div className="mt-3 flex items-start justify-between gap-4">
          <h1 className="font-display text-3xl md:text-5xl leading-tight font-medium">
            {row.name}
          </h1>
          <button
            type="button"
            onClick={() => toggleSaved(row.ciId)}
            aria-label={isSaved ? "Remove from your shortlist" : "Save to your shortlist"}
            aria-pressed={isSaved}
            className={[
              "shrink-0 h-11 w-11 grid place-items-center rounded-full border",
              isSaved
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            <Star className="h-5 w-5" fill={isSaved ? "currentColor" : "none"} />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {[row.family, row.cluster, row.level].filter(Boolean).join(" · ")}
        </p>
        <PinToVisionBoard
          ciId={row.ciId}
          title={row.name}
          family={row.family}
          className="mt-4 sm:max-w-sm"
        />

        {intel?.brief && (
          <p className="mt-5 text-[17px] md:text-lg leading-relaxed text-foreground/90">
            {intel.brief}
          </p>
        )}
        {intel && (
          <div className="mt-5 flex flex-wrap gap-2">
            {intel.growthPct != null && (
              <span className="text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 tabular-nums">
                {Math.round(intel.growthPct)}% growth
              </span>
            )}
            {intel.demand["2040"] && (
              <span className="text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-secondary/60">
                {intel.demand["2040"]} demand by 2040
              </span>
            )}
            {intel.remote && (
              <span className="text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-secondary/60">
                {intel.remote}
              </span>
            )}
            {intel.salaryStart && (
              <span className="text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-secondary/60 tabular-nums">
                Starts {intel.salaryStart}
              </span>
            )}
          </div>
        )}
      </section>

      {/* 1b · Forecast — free for everyone, the reason most people are here */}
      {intel && (
        <Section
          id="forecast"
          eyebrow="Forecast"
          title="Where this is heading"
          subtitle="Demand modelled to 2040, from the sealed career registry."
        >
          <DemandForecast intel={intel} />
        </Section>
      )}



      {/* 2 · Why this matched you — free tier sees this in full */}
      {match && signals.length > 0 ? (
        <Section
          id="why"
          eyebrow="Why this matched you"
          title="Every signal, strongest first"
          subtitle="Twenty things you told us, each scored against this role. Nothing here is hidden from you."
        >
          <div className="rounded-3xl border border-border bg-card p-5 md:p-6 space-y-4">
            {signals.map((s) => (
              <div key={s.key}>
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <p className="text-sm leading-snug text-foreground/90">
                    {s.score >= 55 ? s.copy!.fit : s.copy!.miss}
                  </p>
                  <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                    {s.score}
                  </span>
                </div>
                <ScoreBar value={s.score} />
              </div>
            ))}
          </div>
        </Section>
      ) : (
        <Section
          id="why"
          eyebrow="Why this might matter"
          title="We haven't met you yet"
          subtitle="Everything below describes the role on its own terms."
        >
          <div className="rounded-3xl border border-border bg-card p-5 md:p-6">
            <p className="text-[15px] leading-relaxed text-foreground/90">
              Take the assessment and we'll score this role against what you actually told us —
              signal by signal, including the parts that wouldn't suit you.
            </p>
            <Link
              to="/start"
              className="mt-5 inline-flex items-center justify-center gap-2 min-h-[48px] px-6
                rounded-2xl bg-primary text-primary-foreground font-medium hover:opacity-90"
            >
              Start where you are <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      )}

      {!CAREER_DETAIL_GATED || premium || membership.loading ? gated : <LockedTeaser>{gated}</LockedTeaser>}

      <footer className="mt-16 pt-8 border-t border-border text-sm text-muted-foreground pb-16">
        {row.ciId} · one of 5,877 careers we read you against. Where our evidence is thin we've
        said so on the page rather than filling the gap with a guess.
        <OccupationCoding ciId={row.ciId} className="mb-6 not-italic" />
        <OccupationStandardsNote className="mt-4" />
        <OnetAttribution className="mt-4" />
        {intel && <IntelProvenance intel={intel} />}
        {enrichment && (
          <div className="mt-4 pt-4 border-t border-border/60">
            <p className="text-xs">
              This is an emerging role, so parts of this page were researched from published
              sources rather than derived from an occupational prior:{" "}
              {Object.keys(enrichment.payload)
                .map((k) => ENRICHED_FIELD_LABELS[k as keyof typeof ENRICHED_FIELD_LABELS])
                .filter(Boolean)
                .join(" · ")}
              .
            </p>
            {enrichment.sources.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {enrichment.sources.map((s) => (
                  <li key={s.url}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-foreground"
                    >
                      {s.domain || s.url}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </footer>

    </Shell>
  );
}

/** Short, human date for the run a page's signals came from. */
function formatRunDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "an earlier run";
  return new Date(t).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function findRow(bundle: CareersBundle, ciId: string): CareerRow | undefined {
  if (!ciId) return undefined;
  const key = ciId.toUpperCase();
  return bundle.rows.find((r) => r.ciId.toUpperCase() === key);
}

function Shell({ backTo, children }: { backTo: string; children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground
              min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl px-1"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 md:px-8">{children}</main>
    </div>
  );
}

export default function CareerDetail() {
  return (
    <AssessmentV2Provider>
      <CareerDetailInner />
    </AssessmentV2Provider>
  );
}
